import { isPlatformBrowser } from '@angular/common';
import { Router, ActivationEnd } from '@angular/router';
import { Destroyer, trimUrl, getCookie } from '@apps/tools';
import { takeUntil, filter, map, distinctUntilChanged } from 'rxjs/operators';
import * as i0 from "@angular/core";
import * as i1 from "@angular/router";
/**
 * Warstwa zdarzen porownywarki -> dataLayer (GTM-N64HFT9F, wspolny z landingiem
 * Astro). Kazdy event niesie `portal:'porownywarka'` + `product` (z URL) + `cid`
 * (cookie afiliacji), zeby rozroznic portale w GA4/Clarity. Taksonomia snake_case
 * spojna z landingiem - jeden slownik, jeden trigger GTM, jedne key events.
 *
 * SSR-safe: `window`/`document` istnieja tylko w przegladarce (Angular Universal
 * renderuje na serwerze) - kazdy dostep za guardem.
 */
export class TrafficService extends Destroyer {
    constructor(router, platformId) {
        super();
        this.router = router;
        this.PORTAL = 'porownywarka';
        // Dedup eventow jednorazowych per przebieg lejka (reset przy calculator_start).
        this.firedOnce = new Set();
        // ---- Router: page_view przy nawigacji SPA ----
        //
        // page_view TYLKO przy zmianie SCIEZKI (issue zatrzymajlicznik#17): wczesniej
        // porownywalismy pelny router.url z query params, wiec kazda zmiana filtrow
        // na /porownanie emitowala page_view (7,5 odslony/sesje w GA4 i Clarity).
        // Zmiana samych filtrow (ta sama sciezka, inna query) = offer_filter_change.
        this.lastTrackedPath = null;
        // isPlatformBrowser jest odporny na stuby window/document w SSR
        // (apps/stop-counter-ssr/src/main.ts stubuje globale dla widgetow UMD).
        this.isBrowser = isPlatformBrowser(platformId);
    }
    // ---- Rdzen: push do dataLayer (zero pracy synchronicznej poza push) ----
    push(event, params = {}) {
        if (!this.isBrowser || typeof window === 'undefined')
            return;
        const w = window;
        w.dataLayer = w.dataLayer || [];
        const clean = {};
        const base = this.baseParams();
        const merged = Object.assign({}, base, params);
        Object.keys(merged).forEach(k => {
            const v = merged[k];
            if (v !== undefined && v !== null && v !== '')
                clean[k] = v;
        });
        w.dataLayer.push(Object.assign({ event, portal: this.PORTAL }, clean));
    }
    /** Wspolne wymiary doklejane do kazdego eventu: product (z URL), cid (cookie). */
    baseParams() {
        const params = {};
        const product = this.currentProduct();
        if (product)
            params.product = product;
        // `cid` to zarezerwowana nazwa w GA4 (client_id) - wysylamy jako `aff_cid`.
        const cid = this.currentCid();
        if (cid)
            params.aff_cid = cid;
        return params;
    }
    /** Produkt z URL: /{produkt}/... -> prad|gaz|fotowoltaika. */
    currentProduct() {
        try {
            const seg = trimUrl(this.router.url).split('/')[1];
            return seg || '';
        }
        catch (e) {
            return '';
        }
    }
    /** cid afiliacji z cookie (SSR-safe - getCookie czyta document.cookie). */
    currentCid() {
        if (!this.isBrowser)
            return '';
        try {
            return getCookie('cid') || '';
        }
        catch (e) {
            return '';
        }
    }
    /** Odpala event tylko raz na przebieg lejka (klucz = event[:suffix]). */
    once(key, event, params = {}) {
        if (this.firedOnce.has(key))
            return;
        this.firedOnce.add(key);
        this.push(event, params);
    }
    initRouterEvents() {
        this.router.events
            .pipe(takeUntil(this.destroy$), filter(e => e instanceof ActivationEnd), map(() => this.router.url), distinctUntilChanged())
            .subscribe(url => {
            const path = url.split('?')[0];
            if (path !== this.lastTrackedPath) {
                this.lastTrackedPath = path;
                this.pageView(path);
            }
            else {
                // Ta sama sciezka, inna query -> uzytkownik zmienil filtry ofert.
                this.push('offer_filter_change', { step: 'offers' });
            }
        });
    }
    pageView(url) {
        this.push('page_view', { page_path: url });
    }
    // ---- Lejek kalkulatora (nazwy taksonomii z planu) ----
    /** Wejscie do kalkulatora - reset deduplikacji (nowy przebieg lejka). */
    setCalculatorStart() {
        this.firedOnce.clear();
        this.push('calculator_start', { step: 'form' });
    }
    /** Domkniecie kalkulatora (submit formularza -> wyniki). */
    setCalculatorFinalize() {
        this.push('calculator_finalize');
    }
    /** Wejscie na liste ofert (dawniej Search). */
    setNewSearch(_data) {
        this.once('view_offers', 'view_offers', { step: 'offers' });
    }
    /** Wybor/oglada oferty (dawniej ViewContent). */
    setViewContent(data) {
        this.push('offer_select', {
            offer_id: data && data.offerId ? data.offerId : undefined
        });
    }
    /** Wejscie do formularza kontaktu (dawniej InitiateCheckout). */
    setCheckout(_data) {
        this.once('checkout_start', 'checkout_start', { step: 'contact' });
    }
    // ---- SMS (bramka dla kanalu telefon) ----
    setSmsSent() {
        this.push('sms_sent');
    }
    setSmsVerified() {
        this.push('sms_verified');
    }
    // ---- Bledy ----
    setFormError(errorCode, contactChannel) {
        this.push('form_error', {
            error_code: errorCode,
            contact_channel: contactChannel
        });
    }
    // ---- Konwersja: generate_lead PO sukcesie submission.save ----
    setPurchase(data) {
        this.push('generate_lead', {
            contact_channel: data && data.contactChannel ? data.contactChannel : undefined
        });
    }
    // ---- Post-konwersja ----
    setFileUpload() {
        this.push('file_upload_faktura');
    }
    // ---- Strona kontaktu (opcjonalne) ----
    setContactView() {
        this.push('contact_click', { channel: 'phone' });
    }
}
TrafficService.ngInjectableDef = i0.ɵɵdefineInjectable({ factory: function TrafficService_Factory() { return new TrafficService(i0.ɵɵinject(i1.Router), i0.ɵɵinject(i0.PLATFORM_ID)); }, token: TrafficService, providedIn: "root" });
